import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box,
    CardHeader,
    Avatar,
    IconButton,
    Typography,
    Tooltip,
    CardContent,
    Button,
    CardActions,
    Menu,
    MenuItem,
    useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { PlayArrow, AddCircle, RemoveCircle } from '@mui/icons-material';

import { showToast } from '../reducers/uiReducer';
import { setLoading } from '../reducers/loadingReducer';
import DataTable from './DataTable';
import DatasetDetail from './DatasetDetail';
import {
    postDocument,
    deleteDocument,
    queryDataset,
} from '../services/apiService';
import { addMessage } from '../reducers/messagesReducer';
import SelectedTileHeader from './SelectedTileHeader';

import {
    setShowTiles,
    setIsActiveChat,
    setSelectedTileId,
    addPromptItem,
    removePromptItem,
    setSelectedPromptItem,
    clearSelectedPromptItem,
} from '../reducers/uiReducer';
import { setSelectedDataset } from '../reducers/datasetsReducer';

const DocumentManager = ({ show, onClose, icon, title }) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const loading = useSelector((state) => state.loading);
    const datasets = useSelector((state) => state.datasets);
    const selectedDataset = useSelector(
        (state) => state.datasets.selectedDataset,
    );
    const selectedItem = useSelector((state) => state.ui.selectedPromptItem);

    const [anchorEl, setAnchorEl] = useState(null);
    const [showAddDataset, setShowAddDataset] = useState(false);
    const [showSelectedDataset, setShowSelectedDataset] = useState(false);

    const handleClose = () => {
        dispatch(setSelectedDataset(null));
        setShowAddDataset(false);
        dispatch(setSelectedPromptItem(null));
        onClose();
    };

    const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
    const handleMenuClose = () => setAnchorEl(null);

    const handleRunPrompt = async (prompt) => {
        dispatch(setSelectedDataset(null));
        dispatch(setSelectedTileId(null));
        dispatch(setIsActiveChat(true));
        dispatch(setLoading(true));

        const newUserMessage = {
            role: 'user',
            componentType: 'text',
            content: [{ type: 'text', text: prompt }],
        };
        dispatch(addMessage(newUserMessage));

        try {
            const docId = selectedDataset.id;
            const response = await queryDataset(docId, prompt);
            if (response?.result?.choices?.[0]?.message) {
                dispatch(
                    addMessage({
                        role: 'assistant',
                        componentType: 'text',
                        content: [
                            {
                                type: 'text',
                                text: response.result.choices[0].message.content,
                            },
                        ],
                    }),
                );
            }
        } catch (error) {
            console.error('Failed to send message:', error);
        } finally {
            dispatch(setLoading(false));
        }
    };

    const handleSelectedRow = (row) => {
        dispatch(setSelectedDataset(row));
        setShowSelectedDataset(true);
    };

    const handleShowAddDataset = () => {
        dispatch(setSelectedDataset({}));
        setShowAddDataset(true);
    };

    const handleDelete = async () => {
        dispatch(setLoading(true));
        try {
            await deleteDocument(selectedDataset.id, dispatch);
            dispatch(
                showToast({
                    message: 'Document deleted successfully!',
                    severity: 'success',
                }),
            );
        } catch (error) {
            console.error('Failed to delete document:', error);
            dispatch(
                showToast({
                    message: `Failed to delete document - ${error.response?.data?.error}`,
                    severity: 'error',
                }),
            );
        } finally {
            dispatch(setLoading(false));
        }
        onClose();
    };

    const handleAddPrompt = () => {
        const newPromptItem = {
            id: selectedDataset.id,
            type: 'analyze_dataset',
            showIcon: true,
            config: selectedDataset,
        };
        dispatch(addPromptItem(newPromptItem));
        dispatch(setSelectedPromptItem(newPromptItem));
    };

    const handleRemovePrompt = (id) => {
        dispatch(clearSelectedPromptItem(null));
        dispatch(removePromptItem(id));
    };

    return (
        <Box
            sx={{
                width: '100%',
                height: 450,
                animation: 'fadeIn 0.2s ease-in',
                background:
                    theme.palette.mode === 'dark'
                        ? 'linear-gradient(to bottom, #1e1e1e, #2e2e2e)'
                        : 'linear-gradient(180deg, #fff, #e4f2fb, #f2f2f2)',
                boxShadow: 3,
                borderRadius: 2,
                animation: 'fadeIn 0.4s ease-in-out',
                '@keyframes fadeIn': {
                    from: { opacity: 0 },
                    to: { opacity: 1 },
                },
            }}
        >
            <CardHeader
                sx={{ paddingBottom: 0 }}
                action={
                    <Tooltip title="Cancel">
                        <IconButton onClick={handleClose}>
                            <CloseIcon />
                        </IconButton>
                    </Tooltip>
                }
                title={
                    <SelectedTileHeader
                        icon={icon}
                        title={title}
                        setShowSelectedItem={setSelectedDataset}
                        selectedItem={selectedDataset?.dataSetName || null}
                        setSelectedItem={(doc) => dispatch(setSelectedDataset(doc))}
                    />
                }
            />
            <CardContent sx={{
                overflow: 'hidden', // Prevent content from overflowing
                position: 'relative', // Ensure proper positioning for child elements
            }}>
                {selectedDataset ? (
                    <DatasetDetail selectedDataset={selectedDataset} />
                ) : selectedItem && selectedItem.type === 'analyze_dataset' ? (
                    <DatasetDetail selectedDataset={selectedItem.config} /> 
                ) : (
                    <DataTable
                        userDatasets={datasets.userDatasets}
                        handleSelectedRow={handleSelectedRow}
                        handleShowAddDatast={handleShowAddDataset}
                    />
                )}
            </CardContent>

            {selectedDataset && (
                <CardActions
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        ml: 1,
                        mt: -1,
                        mb: 1,
                    }}
                >


                    <Box>
                        <Button
                            variant="outlined"
                            onClick={handleMenuOpen}
                            endIcon={<KeyboardArrowDownIcon />}
                            disabled={!selectedDataset}
                        >
                            Quick Prompts
                        </Button>
                        <Menu
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl)}
                            onClose={handleMenuClose}
                        >
                            <MenuItem
                                onClick={() =>
                                    handleRunPrompt('Give me a short summary of this dataset.')
                                }
                            >
                                Summary
                            </MenuItem>
                            <MenuItem
                                onClick={() => handleRunPrompt('Review for any patterns.')}
                            >
                                Patterns
                            </MenuItem>
                            <MenuItem onClick={() => handleRunPrompt('Check for anomalies.')}>
                                Anomalies
                            </MenuItem>
                        </Menu>
                    </Box>
                    <Box>
                        {!selectedItem ? (
                            <Tooltip title="Add data to prompt">
                                <Button
                                    variant="contained"
                                    onClick={handleAddPrompt}
                                    startIcon={<AddCircle />}
                                    sx={{ ml: 1 }}
                                >
                                    Add
                                </Button>
                            </Tooltip>
                        ) : (
                            <Tooltip title="Remove data from prompt">
                                <Button
                                    variant="outlined"
                                    onClick={() => handleRemovePrompt(selectedItem.id)}
                                    color="error"
                                    startIcon={<RemoveCircle />}
                                    sx={{ ml: 1 }}
                                >
                                    Remove
                                </Button>
                            </Tooltip>
                        )}
                    </Box>
                </CardActions>
            )}
        </Box>
    );
};

export default DocumentManager;
